import { useState, useEffect } from 'react'

/**
 * Custom hook for responsive media queries using native window.matchMedia API
 *
 * @param query - Media query string (e.g., '(max-width: 768px)')
 * @returns boolean indicating if the media query matches
 *
 * @example
 * ```tsx
 * const isMobile = useMediaQuery('(max-width: 640px)')
 * const isTablet = useMediaQuery('(max-width: 768px)')
 * const isDesktop = useMediaQuery('(min-width: 1024px)')
 * ```
 *
 * Tailwind breakpoints mapping:
 * - sm: '(min-width: 640px)'
 * - md: '(min-width: 768px)'
 * - lg: '(min-width: 1024px)'
 * - xl: '(min-width: 1280px)'
 * - 2xl: '(min-width: 1536px)'
 *
 * For max-width queries:
 * - Mobile: '(max-width: 640px)'
 * - Tablet: '(max-width: 768px)'
 * - Small Desktop: '(max-width: 1024px)'
 */
export function useMediaQuery(query: string): boolean {
  // Handle SSR - return false during server-side rendering
  const [matches, setMatches] = useState<boolean>(false)

  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return

    const mediaQuery = window.matchMedia(query)

    // Set initial value
    setMatches(mediaQuery.matches)

    // Handler for media query changes
    const handler = (event: MediaQueryListEvent) => {
      setMatches(event.matches)
    }

    // Modern browsers support addEventListener
    mediaQuery.addEventListener('change', handler)

    // Cleanup: remove event listener on unmount
    return () => {
      mediaQuery.removeEventListener('change', handler)
    }
  }, [query])

  return matches
}
